import type { FC, ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/browser"
import { easeInOut } from "@productlane/ui/src/helpers/animations"

interface AnimateChangeInHeightProps {
	children: ReactNode
	className?: string
}

export const AnimateChangeInHeight: FC<AnimateChangeInHeightProps> = ({
	children,
	className,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [height, setHeight] = useState<number | "auto">("auto")

	useEffect(() => {
		if (containerRef.current) {
			const resizeObserver = new ResizeObserver((entries) => {
				// We only have one entry, so we can use entries[0].
				const observedHeight = entries[0]?.contentRect.height
				if (observedHeight !== undefined) {
					setHeight(observedHeight)
				}
			})

			resizeObserver.observe(containerRef.current)

			return () => {
				// Cleanup the observer when the component is unmounted
				resizeObserver.disconnect()
			}
		}
	}, [])

	return (
		// The -mx/px pair keeps the clip box wider than the content, so a child's
		// focus ring or shadow is not sliced when it sits flush with the edge.
		<motion.div
			className={cn(className, "-mx-1 overflow-hidden px-1")}
			animate={{ height }}
			transition={{ duration: 0.1, ease: easeInOut }}
		>
			<div ref={containerRef}>{children}</div>
		</motion.div>
	)
}
